/* ============================================================
   session.js — who is listening, and how they got in: the rules (PLAN-2026-09-23-layers §3.6). Room layer: no DOM.
   The page (app.js) still does the jumps and the storage writes; what it decides, it decides here, so node can test it.

     passLink(search, { passLink })      the tester's door: ?token=<mia-data access token> → { token, search } — the token to keep
                                         (only when the station allows the door) and the address without it (a ?code/?state/?invites
                                         riding along must still be read)
     firstName(user)                     the listener's first name, for 'created by …' and the snapshot
     uidOf({ mock, sub, meId })          who is signed in now: mock has one listener; else the token's subject, else /me's id
     carry(text, templates)              the words from before the door (the landing's box): a skill ("/cmd words") → { tool } — its
                                         approval card approves itself (DOOR_GATES); plain words → { prefill } into the field
     doorOrderFor(tool, now)             the order the front door placed: { tool, until } — ten minutes, or the card, ends it
     greeting(name)                      her first words to a new account that came through the door empty-handed (owner 2026-09-22)
     doorHash(line, { gate, carry })     the landing's address with her line, the gate and the carry ("/#signin=…&gate&carry=…")
     proveMe(me, { tries, wait })        a pass link is proven against /me: three tries a blip apart; only a 401 says the pass is dead
                                         → { user } | { dead: true } | { offline: true }
     cleanAddress(hash)                  the room IS "/" (owner 2026-09-11): the address the room writes back — a #signin forwarded by
                                         the landing is the landing's, not ours
   ============================================================ */
export function passLink(search, { passLink: allowed = true } = {}) {
  const q = new URLSearchParams(search || '');
  const t = q.get('token');
  if (!t) return null;
  q.delete('token');
  return { token: allowed ? t.trim() : '', search: q.toString() ? '?' + q : '' };
}

export const firstName = (u) =>
  String((u && u.name) || '')
    .trim()
    .split(/\s+/)[0] || '';

export const uidOf = ({ mock = false, sub = '', meId = '' } = {}) => (mock ? 'mock' : sub || meId);

export function carry(text, templates = []) {
  const t = String(text || '').trim();
  if (!t) return null;
  const m = t.match(/^(\/\w+)\s*([\s\S]*)$/);
  const tpl = m && templates.find((x) => x.cmd === m[1]);
  return tpl ? { text: t, tool: tpl.cmd.slice(1) } : { text: t, prefill: t };
}

export const DOOR_ORDER_MS = 600000;
export const doorOrderFor = (tool, now = Date.now()) => ({ tool, until: now + DOOR_ORDER_MS });

export const greeting = (name) =>
  `hi ${name || 'there'}. i play music and i make it. tell me a mood — i'll build you a playlist. tell me what happened — i'll write you a song.`;

export const doorHash = (line, { gate = false, carry: c = '' } = {}) =>
  '/#signin' +
  (line ? '=' + encodeURIComponent(line) : '') +
  (gate ? '&gate' : '') +
  (c ? '&carry=' + encodeURIComponent(c) : '');

export async function proveMe(me, { tries = 3, wait = (i) => new Promise((r) => setTimeout(r, 800 * (i + 1))) } = {}) {
  for (let i = 0; i < tries; i++) {
    try {
      const u = await me();
      if (u) return { user: u };
    } catch (e) {
      if (e && (e.status === 401 || e.code === 'unauthorized')) return { dead: true };
    }
    if (i < tries - 1)
      await wait(
        i
      ); /* a blip, or an answer with no user in it: the same pause before the next try (review 2026-09-23) */
  }
  return { offline: true };
}

export const cleanAddress = (hash = '') => '/' + (/^#signin/.test(hash) ? '' : hash);
